import React, { useEffect, useRef } from 'react';
import '../assets/css/busJourney.css';
import { useTheme } from '../context/ThemeContext';
import busImg from '../assets/images/misc/bus.png';

const BusJourney = () => {
    const busRef = useRef(null);
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const { theme } = useTheme();

    useEffect(() => {
        const canvas = canvasRef.current;
        const bus = busRef.current;
        const container = containerRef.current;

        if (!canvas || !bus || !container) return;

        const context = canvas.getContext('2d');
        const pathAmplitude = 0; // Straight line (Amplitude 0)
        const pathFrequency = 0.005; // Frequency of the sine wave

        let animationStartY = 0;
        let animationEndY = 0;
        let exclusionZones = [];
        let lastScrollTop = window.scrollY;

        // Set canvas dimensions
        const setCanvasDimensions = () => {
            container.style.height = '0px';

            canvas.width = document.documentElement.clientWidth;
            const docHeight = Math.max(
                document.body.scrollHeight, document.documentElement.scrollHeight,
                document.body.offsetHeight, document.documentElement.offsetHeight,
                document.body.clientHeight, document.documentElement.clientHeight
            );
            canvas.height = docHeight;
            container.style.height = `${docHeight}px`;

            animationStartY = 0;
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

            // Detect Exclusion Zones (Content Blocks)
            const zones = [];
            
            // 1. Specific sections that shouldn't be crossed
            const sectionsToExclude = [
                'bg-counters',
                'site-footer',
                'our-partners'
            ];
            
            sectionsToExclude.forEach(id => {
                const el = document.getElementById(id);
                if (el) {
                    const rect = el.getBoundingClientRect();
                    let endY = rect.bottom + scrollTop;
                    if (id === 'our-partners') {
                        endY -= 60; // Leave 60px of road visible at the bottom of the Clients section
                    }
                    zones.push({ start: rect.top + scrollTop, end: endY });
                }
            });

            // 2. Removed Benefit content blocks exclusion to allow road to pass through

            // Sort and merge overlapping zones
            zones.sort((a, b) => a.start - b.start);
            const mergedZones = [];
            if (zones.length > 0) {
                let currentZone = zones[0];
                for (let i = 1; i < zones.length; i++) {
                    if (zones[i].start < currentZone.end) {
                        currentZone.end = Math.max(currentZone.end, zones[i].end);
                    } else {
                        mergedZones.push(currentZone);
                        currentZone = zones[i];
                    }
                }
                mergedZones.push(currentZone);
            }
            exclusionZones = mergedZones;

            // Calculate end y
            const footer = document.getElementById('site-footer');
            animationEndY = footer ? footer.getBoundingClientRect().top + scrollTop : docHeight;

            drawSineWave();
        };

        const drawSineWave = () => {
            context.clearRect(0, 0, canvas.width, canvas.height);

            const drawSegment = (start, end) => {
                if (start >= end) return;
                
                // Draw Road
                context.beginPath();
                context.setLineDash([]);
                context.lineWidth = 20;
                context.strokeStyle = '#d3d3d3';
                context.moveTo(canvas.width / 2, start);
                for (let y = start; y < end; y++) {
                    const x = pathAmplitude * Math.sin(pathFrequency * y) + canvas.width / 2;
                    context.lineTo(x, y);
                }
                context.stroke();

                // Draw Center Line
                context.beginPath();
                context.setLineDash([10, 15]);
                context.lineWidth = 2;
                context.strokeStyle = '#fff';
                context.moveTo(canvas.width / 2, start);
                for (let y = start; y < end; y++) {
                    const x = pathAmplitude * Math.sin(pathFrequency * y) + canvas.width / 2;
                    context.lineTo(x, y);
                }
                context.stroke();
            };

            // Draw segments BETWEEN exclusion zones
            let lastY = animationStartY;
            exclusionZones.forEach(zone => {
                if (zone.start > lastY) {
                    drawSegment(lastY, zone.start);
                }
                lastY = Math.max(lastY, zone.end);
            });

            if (lastY < animationEndY) {
                drawSegment(lastY, animationEndY);
            }
        };

        const calculateAngle = (y) => {
            const dx = 1;
            const dy = pathAmplitude * (Math.sin(pathFrequency * (y + dx)) - Math.sin(pathFrequency * y));
            return Math.atan2(dy, dx) * (180 / Math.PI);
        };

        let ticking = false;

        const updateBusPosition = () => {
            const bus = busRef.current;
            if (!bus || !canvas) return;

            const scrollTop = window.scrollY;
            const busY = scrollTop + window.innerHeight / 2;
            const isScrollingDown = scrollTop >= lastScrollTop;
            lastScrollTop = scrollTop;

            // Hide bus if it's inside any exclusion zone
            const isInExclusion = exclusionZones.some(zone => busY >= zone.start && busY <= zone.end);

            if (busY < animationStartY || busY > animationEndY || isInExclusion) {
                bus.style.opacity = '0';
                bus.style.visibility = 'hidden';
                ticking = false;
                return;
            } else {
                bus.style.opacity = '1';
                bus.style.visibility = 'visible';
            }

            const busX = pathAmplitude * Math.sin(pathFrequency * busY) + (canvas.width / 2) - (bus.offsetWidth / 2);
            const renderY = busY - (bus.offsetHeight / 2);
            let angle = calculateAngle(busY);

            // Flip the bus when scrolling up so it doesn't look like it's reversing
            if (!isScrollingDown) {
                angle += 180;
            }

            // Smoother and more responsive transition
            bus.style.transition = 'transform 0.5s ease-out';
            bus.style.transform = `translate(${busX}px, ${renderY}px) rotate(${angle}deg)`;
            bus.style.display = 'block';
            ticking = false;
        };

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(updateBusPosition);
                ticking = true;
            }
        };

        // Initialize ResizeObserver to handle dynamic content changes (e.g., sliders loading)
        const resizeObserver = new ResizeObserver(() => {
            setCanvasDimensions();
        });
        resizeObserver.observe(document.body);

        // Initial setup
        setCanvasDimensions();
        // Recalculate on load to ensure #about is positioned correctly
        window.addEventListener('load', setCanvasDimensions);
        window.addEventListener('resize', setCanvasDimensions);
        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial call to show bus if in view

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('load', setCanvasDimensions);
            window.removeEventListener('resize', setCanvasDimensions);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [theme]);

    return (
        <div ref={containerRef} className="bus-journey-overlay">
            <img ref={busRef} src={busImg} alt="Bus" className="bus" id="bus" />
            <canvas ref={canvasRef} id="sineWaveCanvas"></canvas>
            <section className="padding_top padding_bottom" style={{ maxWidth: '1140px', marginLeft: 'auto', marginRight: 'auto', display: 'none' }}>
                {/* Content hidden/removed for overlay mode */}
            </section>
        </div>
    );
};

export default BusJourney;
